import {
    CategoryScale, Chart, LinearScale, LineController, LineElement, PointElement, Tooltip,
} from 'chart.js';

class LineChart {
    constructor(element, data, type) {
        this.element = element;
        this.barType = type;

        this.chart = null;
        this.data = data;
        this.labels = [];

        this.gas_prices = [];
        this.electricity_prices = [];

        this.visibleType = ['gas-monthly', 'gas-electricity-monthly'].includes(type) ? 'gas' : 'electricity';
        // Html elements
        this.canvasElement = null;
        this.reloadButton = null;
        this.titleElement = null;
        // Toggles
        this.showGasToggle = null;
        this.showElectricityToggle = null;
        // Colors
        this.gridmainColor = '#C4E3ED';
        this.mainColor = '#006484';
        this.accentColor = '#EDF7FD';
    }

    bootstrap() {
        this.canvasElement = this.element.querySelector('canvas#price-chart');
        this.reloadButton = this.element.querySelector('.reload-chart-button');
        this.titleElement = this.element.querySelector('[data-replace="title"]');

        this.reloadButton.addEventListener('click', (e) => {
            e.preventDefault();
            this.loadChart();
        });

        this.showGasToggle = this.element.querySelector('#show-gas');
        this.showElectricityToggle = this.element.querySelector('#show-electricity');

        if (this.showGasToggle && this.showElectricityToggle) {
            this.showGasToggle.addEventListener('change', () => {
                this.setVisibleType('gas');
            });
            this.showElectricityToggle.addEventListener('change', () => {
                this.setVisibleType('electricity');
            });
        }
    }

    init() {
        this.bootstrap();
        this.initChart();
        this.loadChart();
    }

    setChartData() {
        this.gas_prices = [];
        this.electricity_prices = [];
        this.labels = [];

        for (let index = 0; index < this.data.length; index++) {
            if (this.data[index].gas === null || this.data[index].electricity_normal === null) {
                continue;
            }
            const date = new Date(Date.parse(this.data[index].month));
            this.labels.push(date.toLocaleDateString('nl-NL', {
                month: 'long',
                year: 'numeric',
            }));
            this.gas_prices.push(this.data[index].gas);
            this.electricity_prices.push(this.data[index].electricity_normal);
        }

        if (this.chart) {
            this.chart.data.labels = this.labels;
            this.chart.data.datasets[0].data = this.gas_prices;
            this.chart.data.datasets[1].data = this.electricity_prices;

            if (this.visibleType === 'gas') {
                this.chart.setDatasetVisibility(0, true);
                this.chart.setDatasetVisibility(1, false);
            } else {
                this.chart.setDatasetVisibility(0, false);
                this.chart.setDatasetVisibility(1, true);
            }
        }
    }

    updateChart() {
        if (!this.chart) {
            return;
        }
        this.setChartData();
        setTimeout(() => {
            this.chart.update();
        }, 100);
    }

    updateState() {
        if (this.loading) {
            this.element.classList.add('loading');
        } else {
            this.element.classList.remove('loading');
        }
        if (this.error) {
            this.element.classList.add('error');
        } else {
            this.element.classList.remove('error');
        }
    }

    initChart() {
        this.chart = new Chart(
            this.canvasElement,
            {
                type: 'line',
                options: {
                    maintainAspectRatio: false,
                    borderColor: this.mainColor,
                    onResize: () => {
                        this.updateChart();
                    },
                    pointRadius: 5,
                    pointBorderWidth: 2,
                    pointBorderColor: this.mainColor,
                    backgroundColor: this.accentColor,
                    pointHoverRadius: 8,
                    pointHoverBorderWidth: 2,
                    pointHoverBorderColor: this.mainColor,
                    pointStyle: 'circle',
                    tension: 0.4,
                    scales: {
                        x: {
                            grid: {
                                display: false,
                            },
                            ticks: {
                                maxRotation: 50,
                                minRotation: 50,
                                color: '#171717',
                                font: {
                                    size: 14,
                                    weight: 400,
                                },

                            },
                        },
                        y: {
                            padding: 0,
                            beginAtZero: false,
                            grace: '20%',
                            grid: {
                                color: this.gridmainColor,
                            },
                            ticks: {
                                color: '#171717',
                                font: {
                                    size: 13,
                                    weight: 400,
                                },
                                // Include a dollar sign in the ticks
                                callback(value) {
                                    return value.toLocaleString('nl-NL', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 2,
                                    });
                                },
                            },
                        },
                    },
                    interaction: {
                        mode: 'point',
                        intersect: false,
                    },
                    animation: {
                        y: {
                            duration: 1000,
                            easing: 'easeOutQuart',
                        },
                    },
                    plugins: {
                        tooltip: {
                            mode: 'index',
                            intersect: false,
                            backgroundColor: this.mainColor,
                            caretPadding: 16,
                            caretSize: 8,
                            titleFont: {
                                size: 16,
                                weight: 600,
                                color: '#fff',
                            },

                            titleMarginBottom: 8,
                            padding: {
                                x: 16,
                                y: 8,
                            },
                            displayColors: false,
                            bodyFont: {
                                size: 16,
                                weight: 400,
                                color: '#fff',
                            },
                            callbacks: {
                                label: (tooltipItem) => {
                                    const apendage = this.visibleType === 'gas' ? ' per m3' : ' per kWh';
                                    return tooltipItem.raw.toLocaleString('nl-NL', {
                                        style: 'currency',
                                        currency: 'EUR',
                                        minimumFractionDigits: 2,
                                    }) + apendage;
                                },
                            },
                        },
                    },
                },
                data: {
                    labels: this.labels,
                    datasets: [
                        {
                            data: this.gas_prices,
                            hidden: this.visibleType === 'electricity',
                        },
                        {
                            data: this.electricity_prices,
                            hidden: this.visibleType === 'gas',
                        },
                    ],
                },
            },
        );
    }

    setVisibleType(type) {
        this.visibleType = type;
        if (this.titleElement) {
            this.titleElement.innerHTML = type === 'gas' ? 'Gemiddelde gasprijs afgelopen jaar' : 'Gemiddelde stroomprijs afgelopen jaar';
        }
        this.updateChart();
    }

    fetchData() {
        fetch('/ajax/canary/prices-average-year')
            .then((response) => {
                if (response.ok) {
                    return response.json();
                }
                return Promise.reject(response);
            })
            .then((json) => {
                this.data = json;
                this.setChartData();
                return Promise.resolve(json);
            })
            .then(() => {
                this.updateChart();
            })
            .catch(() => {
                this.error = true;
                Promise.resolve(false);
            })
            .finally(() => {
                this.loading = false;
                this.updateState();
            });
    }

    loadChart() {
        this.loading = true;
        this.error = false;
        this.updateState();

        if (this.data?.length) {
            this.setChartData();
            this.updateChart();
            setTimeout(() => {
                this.loading = false;
                this.updateState();
            }, 100);
        } else {
            this.fetchData();
        }
    }

    handleScreenWidthChange(event) {
        this.updateChart();
    }

    resizeWatcher() {
        const mediaQuery = window.matchMedia('(min-width: 992px)');

        mediaQuery.addEventListener('change', () => {
            this.handleScreenWidthChange(mediaQuery);
        });
    }
}

export function lineChart(chartElement, data = []) {
    Chart.register(
        LineController,
        CategoryScale,
        LinearScale,
        LineElement,
        PointElement,
        Tooltip,
    );

    const type = chartElement.dataset.chartType;
    const chartBlockInstance = new LineChart(chartElement, data, type);
    chartBlockInstance.init();
    chartBlockInstance.resizeWatcher();
}
